import { CloseCircleOutlined, DownOutlined, InfoCircleOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Dropdown, Menu, Popover } from "antd";
import moment from "moment";
import React from "react";
import { Booking } from "../../../booking/Booking.model";
import { SchedulingPreference } from "../../../calendar/Calendar.model";
import VideoSession, { ExpertSessionRole } from "../../../model/ExpertSession";
import {
  VideoSessionRescheduleExpertMenuItem,
  VideoSessionRescheduleMenteeMenuItem,
} from "./VideoSessionReschedule.component";

interface VideoSessionManageProps {
  vs: VideoSession;
  bkng: Booking;
}

export function VideoSessionManageComponent(props: VideoSessionManageProps) {
  const now = moment();
  const vs = props.vs;
  const bkng = props.bkng;

  // Show for active calendar-based sessions that have actions available:
  // - canReschedule: normal reschedule window
  // - expertRescheduleMarked: mentee needs to pick a new slot
  if (
    vs.schedulingPreference !== SchedulingPreference.CALENDAR ||
    (!vs.canReschedule && !vs.expertRescheduleMarked)
  ) {
    return null;
  }

  const renderDeadlineInfo = (deadline?: string, policy?: string) => {
    if (!policy) return null;
    if (!deadline) return <div>{policy}</div>;

    const deadlineMoment = moment(deadline);
    if (now.isBefore(deadlineMoment)) {
      const duration = moment.duration(deadlineMoment.diff(now));
      let remainingStr = "";
      if (duration.asHours() < 48) {
        remainingStr = `${Math.floor(duration.asHours())} hours ${duration.minutes()} mins`;
      } else {
        remainingStr = `${Math.floor(duration.asDays())} days ${Math.floor(duration.asHours() % 24)} hours`;
      }
      return (
        <div>
          <div>{policy}</div>
          <div className="text-info mt-1">
            <b>Remaining time:</b> {remainingStr}
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <div>{policy}</div>
          <div className="text-danger font-weight-bold">
            No longer possible for this session.
          </div>
        </div>

      );
    }
  };

  const policyContent = (
    <div style={{ maxWidth: "300px" }}>
      {vs.modificationPolicy && (
        <div className="mb-2">
          {renderDeadlineInfo(vs.modificationDeadline, vs.modificationPolicy)}
          {vs.remainingReschedules !== undefined && (
            <div className={`${vs.remainingReschedules === 0 ? "text-danger" : "text-info"} mt-1`}>
              <b>Remaining Reschedules:</b> {vs.remainingReschedules}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const isExpert = bkng.bcd.role === ExpertSessionRole.EXPERT || bkng.bcd.role === ExpertSessionRole.ADMIN;
  const cancelContent = isExpert ? (
    <div style={{ maxWidth: "300px" }}>
      <p className="mb-0">
        To cancel this session, please contact our team via the{" "}
        <a href="https://enginebogie.com/help" target="_blank" rel="noreferrer">
          Help Page
        </a>.
      </p>
    </div>
  ) : (
    <div style={{ maxWidth: "300px" }}>
      <p>
        To cancel this session, please contact our team via the{" "}
        <a href="https://enginebogie.com/help" target="_blank" rel="noreferrer">
          Help Page
        </a>.
      </p>
      <p className="mb-0 text-muted" style={{ fontSize: "0.85rem" }}>
        Note: Cancellation will be processed as per the expert's cancellation policy shown in the "View Policies" section.
      </p>
    </div>
  );

  const menu = (
    <Menu>
      {(bkng.bcd.role === ExpertSessionRole.EXPERT || bkng.bcd.role === ExpertSessionRole.ADMIN) && !vs.expertRescheduleMarked && (
        <Menu.Item key="expert-res">
          <VideoSessionRescheduleExpertMenuItem session={vs} bcd={bkng.bcd} />
        </Menu.Item>
      )}
      {(bkng.bcd.role === ExpertSessionRole.MENTEE || bkng.bcd.role === ExpertSessionRole.ADMIN) && (
        <Menu.Item key="mentee-res">
          <VideoSessionRescheduleMenteeMenuItem session={vs} bcd={bkng.bcd} />
        </Menu.Item>
      )}
      <Menu.Item key="cancel" icon={<CloseCircleOutlined />}>
        <Popover content={cancelContent} title="Cancel Session" trigger="click" placement="left">
          <span>
            Cancel Session
          </span>
        </Popover>
      </Menu.Item>
      {!isExpert && (
        <Menu.Item key="policies" icon={<InfoCircleOutlined />}>
          <Popover content={policyContent} title={<b>Modification Policies:</b>} trigger="click" placement="left">
            <span>
              View Policies
            </span>
          </Popover>
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <div>
      <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
        <Button
          type="primary"
          className="d-flex justify-content-between align-items-center"
        >
          <SettingOutlined />
          <span>Manage Session</span>
          <DownOutlined />
        </Button>
      </Dropdown>
    </div>
  );
}
